import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { X, TrendingUp, Plus, Search, ChevronDown, GripHorizontal } from 'lucide-react';
import questdbService from '../../../services/questdb';

const CHART_COLORS = ['#667eea', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

const TIME_RANGES = [
  { label: '1 min',    value: '1m',     minutes: 1,     sampleBy: null  },
  { label: '5 min',    value: '5m',     minutes: 5,     sampleBy: null  },
  { label: '15 min',   value: '15m',    minutes: 15,    sampleBy: null  },
  { label: '30 min',   value: '30m',    minutes: 30,    sampleBy: null  },
  { label: '1 hour',   value: '1h',     minutes: 60,    sampleBy: null  },
  { label: '6 hours',  value: '6h',     minutes: 360,   sampleBy: '2m'  },
  { label: '24 hours', value: '24h',    minutes: 1440,  sampleBy: '8m'  },
  { label: '7 days',   value: '7d',     minutes: 10080, sampleBy: '1h'  },
  { label: 'Custom',   value: 'custom', minutes: null,  sampleBy: null  },
];

const TIMEZONES = [
  { label: 'UTC', tz: 'UTC' },
  { label: 'IST', tz: 'Asia/Kolkata' },
];

// Format for datetime-local input in UTC (YYYY-MM-DDTHH:mm)
const toDatetimeLocalUTC = (d) => {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

// Reliable timezone-aware formatters using Intl.DateTimeFormat
const fmtTime = (ms, tz) => new Intl.DateTimeFormat('en-US', {
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: tz
}).format(new Date(ms));

const fmtFull = (ms, tz) => new Intl.DateTimeFormat('en-US', {
  month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: tz
}).format(new Date(ms));

export default function TagChartPopup({ tags, position, onClose, darkMode, plantId, componentLabel }) {
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState(TIME_RANGES[3]); // default 30m
  const [timezone, setTimezone] = useState('UTC');
  const [showDots, setShowDots] = useState(false);
  const [selectedTags, setSelectedTags] = useState(() => new Set(tags));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Custom range — always stored/displayed in UTC
  const now = new Date();
  const thirtyMinAgo = new Date(now.getTime() - 30 * 60 * 1000);
  const [fromTs, setFromTs] = useState(toDatetimeLocalUTC(thirtyMinAgo));
  const [toTs, setToTs] = useState(toDatetimeLocalUTC(now));

  // Comparison tags
  const [availableCols, setAvailableCols] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [compareSearch, setCompareSearch] = useState('');
  const [comparisonTags, setComparisonTags] = useState(new Set());
  const [tagColors, setTagColors] = useState({});
  const getTagColor = (tag) => tagColors[tag] || CHART_COLORS[allActiveTags.indexOf(tag) % CHART_COLORS.length];
  const setTagColor = (tag, color) => setTagColors(prev => ({ ...prev, [tag]: color }));

  const popupRef = useRef(null);
  const [pos, setPos] = useState({ x: position.x, y: position.y });
  const [size, setSize] = useState({ width: 560, height: 420 });
  const dragRef = useRef(null);
  const isDragging = useRef(false);
  const resizeRef = useRef(null);
  const isResizing = useRef(false);

  const handleHeaderMouseDown = (e) => {
    if (e.target.closest('button') || e.target.closest('select')) return;
    e.preventDefault();
    isDragging.current = true;
    dragRef.current = { startMouseX: e.clientX, startMouseY: e.clientY, startPosX: pos.x, startPosY: pos.y };
  };

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isResizing.current = true;
    resizeRef.current = { startX: e.clientX, startY: e.clientY, startW: size.width, startH: size.height };
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (isDragging.current && dragRef.current) {
        const dx = e.clientX - dragRef.current.startMouseX;
        const dy = e.clientY - dragRef.current.startMouseY;
        setPos({ x: Math.max(0, dragRef.current.startPosX + dx), y: Math.max(0, dragRef.current.startPosY + dy) });
      }
      if (isResizing.current && resizeRef.current) {
        const dw = e.clientX - resizeRef.current.startX;
        const dh = e.clientY - resizeRef.current.startY;
        setSize({
          width: Math.max(400, resizeRef.current.startW + dw),
          height: Math.max(320, resizeRef.current.startH + dh)
        });
      }
    };
    const onMouseUp = () => { isDragging.current = false; isResizing.current = false; };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const theme = darkMode ? {
    bg: '#0f1117', card: '#1a1d29', text: '#e5e7eb', textSecondary: '#9ca3af',
    border: '#374151', accent: '#667eea', hover: '#232837', gridColor: '#1f2937',
    input: '#0f1117', danger: '#ef4444'
  } : {
    bg: '#ffffff', card: '#f9fafb', text: '#111827', textSecondary: '#6b7280',
    border: '#e5e7eb', accent: '#667eea', hover: '#f3f4f6', gridColor: '#f3f4f6',
    input: '#ffffff', danger: '#ef4444'
  };

  // Fetch available columns for comparison
  useEffect(() => {
    const fetchCols = async () => {
      try {
        const sample = await questdbService.query(`SELECT * FROM scada_wide LIMIT 1`, plantId);
        if (Array.isArray(sample) && sample.length > 0) {
          const cols = Object.keys(sample[0]).filter(c => c !== 'timestamp' && c !== 'time' && c !== 'fullTime');
          setAvailableCols(cols);
        }
      } catch (err) {
        console.error('TagChartPopup: failed to fetch columns', err);
      }
    };
    fetchCols();
  }, [plantId]);

  // All tags currently charted = base tags + comparison tags
  const allActiveTags = [...new Set([...selectedTags, ...comparisonTags])];

  useEffect(() => {
    if (allActiveTags.length === 0) { setData([]); setLoading(false); return; }

    // Helper: convert raw QuestDB timestamp value → epoch ms
    // Handles: microsecond integer (from cast…as long), or QuestDB string "YYYY-MM-DD HH:mm:ss.ffffff" (UTC, no Z)
    const rawToMs = (raw) => {
      if (typeof raw === 'number' || (typeof raw === 'string' && /^\d+$/.test(String(raw).trim()))) {
        const n = Number(raw);
        return n > 1e15 ? Math.floor(n / 1000) : n > 1e12 ? n : n * 1000;
      }
      // QuestDB string format: "2026-03-09 06:07:21.000000" — space separator, always UTC, no Z
      let s = String(raw).trim()
        .replace(' ', 'T')              // space → T
        .replace(/(\.\d{3})\d*/, '$1'); // trim sub-ms precision
      if (!s.endsWith('Z') && !s.includes('+') && !/[+-]\d\d:\d\d$/.test(s)) s += 'Z';
      return new Date(s).getTime();
    };

    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        // Select timestamp as microseconds (plain LONG — bypasses formatTimestamp quirks)
        // plus the tag columns
        const tagCols = allActiveTags.join(', ');
        let whereClause;
        if (timeRange.value === 'custom') {
          // inputs are entered in UTC — append 'Z' so browser treats as UTC
          const from = new Date(fromTs + ':00Z').toISOString();
          const to   = new Date(toTs   + ':00Z').toISOString();
          whereClause = `timestamp BETWEEN '${from}' AND '${to}'`;
        } else {
          // Get latest timestamp as epoch microseconds (avoids formatTimestamp issues)
          const latestRes = await questdbService.query(
            `SELECT cast(MAX(timestamp) as long) as latest_us FROM scada_wide`, plantId
          );
          const latestUs = latestRes?.[0]?.latest_us;
          if (!latestUs) { setData([]); setLoading(false); return; }
          const latestMs = rawToMs(latestUs);
          const latestIso = new Date(latestMs).toISOString();
          whereClause = `timestamp >= dateadd('m', -${timeRange.minutes}, '${latestIso}') AND timestamp <= '${latestIso}'`;
        }
        // For large ranges use SAMPLE BY to downsample; for short ranges use raw rows
        const sampleBy = timeRange.sampleBy;
        let sql;
        if (sampleBy) {
          // `timestamp` must appear in SELECT for SAMPLE BY; also cast to LONG so rawToMs gets a
          // pure numeric value (bypasses formatTimestamp's local-time interpretation of QuestDB strings)
          const aggCols = allActiveTags.map(t => `avg(${t}) as ${t}`).join(', ');
          sql = `SELECT timestamp, cast(timestamp as long) as ts_us, ${aggCols} FROM scada_wide WHERE ${whereClause} SAMPLE BY ${sampleBy} FILL(NONE)`;
        } else {
          // cast → LONG integer (microseconds); bypasses formatTimestamp entirely
          sql = `SELECT cast(timestamp as long) as ts_us, ${tagCols} FROM scada_wide WHERE ${whereClause} ORDER BY timestamp ASC LIMIT 2000`;
        }
        const result = await questdbService.query(sql, plantId);
        if (cancelled) return;

        const formatted = result.map(row => {
          const entry = {};
          // sampleBy path: row.timestamp (QuestDB string); raw path: row.ts_us (LONG microseconds)
          const rawTs = row.ts_us !== undefined ? row.ts_us : row.timestamp;
          if (rawTs != null) {
            const ms = rawToMs(rawTs);
            entry.time = ms;
            entry.fullTime = fmtFull(ms, timezone);
          }
          allActiveTags.forEach(tag => {
            const v = row[tag];
            entry[tag] = v !== undefined && v !== null ? parseFloat(v) : null;
          });
          return entry;
        });
        setData(formatted);
      } catch (err) {
        console.error('TagChartPopup fetch error:', err);
        setData([]);
      }
      setLoading(false);
    };

    fetchData();
    // Only auto-refresh for live ranges, not custom
    const interval = timeRange.value !== 'custom' ? setInterval(fetchData, 5000) : null;
    return () => { cancelled = true; if (interval) clearInterval(interval); };
  }, [selectedTags, comparisonTags, timeRange, fromTs, toTs, timezone, plantId]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      return next;
    });
  };

  const toggleComparisonTag = (tag) => {
    setComparisonTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      return next;
    });
  };

  const inputStyle = {
    padding: '4px 8px', background: theme.input, border: `1px solid ${theme.border}`,
    borderRadius: '6px', color: theme.text, fontSize: '11px', cursor: 'pointer', outline: 'none'
  };

  const renderChart = () => {
    if (loading && data.length === 0) {
      return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.textSecondary, fontSize: '12px' }}>Loading...</div>;
    }
    if (data.length === 0) {
      return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.textSecondary, fontSize: '12px' }}>No data in this time range</div>;
    }

    const dotProps = showDots ? { r: 2.5, strokeWidth: 0 } : false;
    const commonProps = { data, margin: { top: 5, right: 10, left: -10, bottom: 0 } };
    const axisProps = {
      xAxis: <XAxis dataKey="time" type="number" domain={['dataMin', 'dataMax']}
        tickFormatter={(v) => fmtTime(v, timezone)}
        tick={{ fontSize: 9, fill: theme.textSecondary }} />,
      yAxis: <YAxis tick={{ fontSize: 9, fill: theme.textSecondary }} width={40} />,
      grid: <CartesianGrid strokeDasharray="3 3" stroke={theme.gridColor} />,
      tooltip: <Tooltip
        contentStyle={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '11px', color: theme.text }}
        labelStyle={{ color: theme.textSecondary, fontSize: '10px' }}
        labelFormatter={(label, payload) => payload?.[0]?.payload?.fullTime || label}
      />,
      legend: allActiveTags.length > 1 ? <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '4px' }} /> : null
    };

    if (chartType === 'area') {
      return (
        <AreaChart {...commonProps}>
          {axisProps.grid}{axisProps.xAxis}{axisProps.yAxis}{axisProps.tooltip}{axisProps.legend}
          {allActiveTags.map((tag) => (
            <Area key={tag} type="monotone" dataKey={tag} stroke={getTagColor(tag)}
              fill={getTagColor(tag)} fillOpacity={0.15} strokeWidth={2} dot={dotProps} activeDot={{ r: 4 }} connectNulls />
          ))}
        </AreaChart>
      );
    }
    if (chartType === 'bar') {
      return (
        <BarChart {...commonProps}>
          {axisProps.grid}{axisProps.xAxis}{axisProps.yAxis}{axisProps.tooltip}{axisProps.legend}
          {allActiveTags.map((tag) => (
            <Bar key={tag} dataKey={tag} fill={getTagColor(tag)} opacity={0.85} />
          ))}
        </BarChart>
      );
    }
    return (
      <LineChart {...commonProps}>
        {axisProps.grid}{axisProps.xAxis}{axisProps.yAxis}{axisProps.tooltip}{axisProps.legend}
        {allActiveTags.map((tag) => (
          <Line key={tag} type="monotone" dataKey={tag} stroke={getTagColor(tag)}
            strokeWidth={2} dot={dotProps} activeDot={{ r: 4 }} connectNulls />
        ))}
      </LineChart>
    );
  };

  // Exclude already-active base tags (but keep them if user might want to compare)
  // Only exclude tags that are already in selectedTags (base chart), not all possible tags
  const filteredCols = availableCols.filter(c =>
    !selectedTags.has(c) &&
    (compareSearch === '' || c.toLowerCase().includes(compareSearch.toLowerCase()))
  );

  const popup = (
    <div
      ref={popupRef}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: size.width,
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: '12px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header — draggable title row + controls row */}
      <div onMouseDown={handleHeaderMouseDown} style={{ cursor: 'grab', userSelect: 'none' }}>
        {/* Row 1: title + close */}
        <div style={{ padding: '8px 10px 4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <TrendingUp size={14} color={theme.accent} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '12px', fontWeight: '700', color: theme.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {componentLabel || 'Tag Chart'}
          </span>
          {loading && <span style={{ fontSize: '10px', color: theme.textSecondary, whiteSpace: 'nowrap', flexShrink: 0 }}>updating…</span>}
          <button onClick={onClose} style={{ padding: '3px', background: 'transparent', border: 'none', color: theme.textSecondary, cursor: 'pointer', flexShrink: 0 }}>
            <X size={14} />
          </button>
        </div>

        {/* Row 2: controls — wrap on narrow widths */}
        <div style={{ padding: '0 10px 8px', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          {/* Chart type */}
          <div style={{ display: 'flex', borderRadius: '5px', overflow: 'hidden', border: `1px solid ${theme.border}`, flexShrink: 0 }}>
            {['line', 'area', 'bar'].map(t => (
              <button key={t} onClick={() => setChartType(t)} style={{
                padding: '3px 8px', border: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: '600',
                background: chartType === t ? theme.accent : theme.card,
                color: chartType === t ? 'white' : theme.textSecondary
              }}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {/* Dots toggle */}
          <button onClick={() => setShowDots(v => !v)} style={{
            padding: '3px 8px', border: `1px solid ${showDots ? theme.accent : theme.border}`,
            borderRadius: '5px', cursor: 'pointer', fontSize: '10px', fontWeight: '600', flexShrink: 0,
            background: showDots ? theme.accent : theme.card,
            color: showDots ? 'white' : theme.textSecondary
          }}>● Dots</button>

          {/* Time range dropdown */}
          <select value={timeRange.value} onChange={e => setTimeRange(TIME_RANGES.find(r => r.value === e.target.value))}
            style={{ ...inputStyle, fontSize: '10px', padding: '3px 6px', flexShrink: 0 }}>
            {TIME_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>

          {/* Timezone */}
          <select value={timezone} onChange={e => setTimezone(e.target.value)}
            style={{ ...inputStyle, fontSize: '10px', padding: '3px 6px', flexShrink: 0 }}>
            {TIMEZONES.map(tz => <option key={tz.label} value={tz.tz}>{tz.label}</option>)}
          </select>
        </div>
      </div>

      {/* Custom from-to row */}
      {timeRange.value === 'custom' && (
        <div style={{ padding: '8px 14px', borderBottom: `1px solid ${theme.border}`, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', fontWeight: '600', color: theme.textSecondary }}>From <span style={{ fontWeight: '400', fontSize: '10px' }}>(UTC)</span></span>
          <input
            type="datetime-local"
            value={fromTs}
            onChange={e => setFromTs(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          />
          <span style={{ fontSize: '11px', fontWeight: '600', color: theme.textSecondary }}>To <span style={{ fontWeight: '400', fontSize: '10px' }}>(UTC)</span></span>
          <input
            type="datetime-local"
            value={toTs}
            onChange={e => setToTs(e.target.value)}
            style={{ ...inputStyle, flex: 1 }}
          />
        </div>
      )}

      {/* Tag color pickers + toggles */}
      <div style={{ padding: '6px 14px', borderBottom: `1px solid ${theme.border}`, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        {tags.map((tag) => {
          const color = getTagColor(tag);
          const active = selectedTags.has(tag);
          return (
            <label key={tag} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '11px', color: active ? color : theme.textSecondary }}>
              <input type="color" value={color} onChange={(e) => setTagColor(tag, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '16px', height: '16px', border: 'none', borderRadius: '3px', padding: 0, cursor: 'pointer', background: 'none' }} />
              {tags.length > 1 && (
                <input type="checkbox" checked={active} onChange={() => toggleTag(tag)}
                  style={{ width: '12px', height: '12px', accentColor: color }} />
              )}
              {tag}
            </label>
          );
        })}
      </div>

      {/* Comparison tags section */}
      <div style={{ borderBottom: `1px solid ${theme.border}` }}>
        <button
          onClick={() => setShowCompare(v => !v)}
          style={{
            width: '100%', padding: '6px 14px', background: 'transparent', border: 'none',
            display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
            color: theme.textSecondary, fontSize: '11px', fontWeight: '600'
          }}
        >
          <Plus size={12} />
          Compare with other tags
          {comparisonTags.size > 0 && (
            <span style={{
              background: theme.accent, color: 'white', borderRadius: '10px',
              padding: '1px 6px', fontSize: '10px', marginLeft: '4px'
            }}>{comparisonTags.size}</span>
          )}
          <ChevronDown size={12} style={{ marginLeft: 'auto', transform: showCompare ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        </button>

        {showCompare && (
          <div style={{ padding: '0 14px 10px' }}>
            {/* Search */}
            <div style={{ position: 'relative', marginBottom: '8px' }}>
              <Search size={12} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: theme.textSecondary }} />
              <input
                type="text"
                placeholder="Search tags..."
                value={compareSearch}
                onChange={e => setCompareSearch(e.target.value)}
                style={{ ...inputStyle, width: '100%', paddingLeft: '26px', boxSizing: 'border-box' }}
              />
            </div>

            {/* Active comparison tags */}
            {comparisonTags.size > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                {[...comparisonTags].map((tag) => {
                  const color = getTagColor(tag);
                  return (
                    <span key={tag} style={{
                      display: 'flex', alignItems: 'center', gap: '4px',
                      padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                      background: color + '22', color: color,
                      border: `1px solid ${color}44`
                    }}>
                      <input type="color" value={color} onChange={(e) => setTagColor(tag, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '14px', height: '14px', border: 'none', borderRadius: '2px', padding: 0, cursor: 'pointer', background: 'none', flexShrink: 0 }} />
                      {tag}
                      <button onClick={() => toggleComparisonTag(tag)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color, lineHeight: 1, fontSize: '13px' }}>×</button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Tag list */}
            <div style={{ maxHeight: '80px', overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {filteredCols.length === 0 && (
                <span style={{ fontSize: '11px', color: theme.textSecondary }}>No tags found</span>
              )}
              {filteredCols.map((col) => {
                const isSelected = comparisonTags.has(col);
                const color = getTagColor(col);
                return (
                  <button
                    key={col}
                    onClick={() => toggleComparisonTag(col)}
                    style={{
                      padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '500', cursor: 'pointer',
                      border: `1px solid ${isSelected ? color : theme.border}`,
                      background: isSelected ? color + '22' : theme.card,
                      color: isSelected ? color : theme.textSecondary
                    }}
                  >
                    {col}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Chart — explicit height so ResponsiveContainer can measure correctly */}
      <div style={{ height: `${size.height - 120}px`, padding: '8px 4px 4px', flexShrink: 0 }}>
        {allActiveTags.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.textSecondary, fontSize: '12px', textAlign: 'center', padding: '0 16px' }}>
            No tags configured.<br />Open the designer and assign tags to this component.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>

      {/* Resize handle — bottom-right corner */}
      <div
        onMouseDown={handleResizeMouseDown}
        style={{
          position: 'absolute', bottom: 0, right: 0,
          width: '18px', height: '18px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'nwse-resize', color: theme.textSecondary, opacity: 0.6,
          transform: 'rotate(90deg)'
        }}
        title="Drag to resize"
      >
        <GripHorizontal size={12} />
      </div>
    </div>
  );

  return createPortal(popup, document.body);
}
